import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './drop-zone.scss';

import { useDropzone } from 'react-dropzone';

import Typography, {
  TypographyStates,
  TypographyWeights,
} from '../Typography/Typography';
import Thumbnail from '../Thumbnail/Thumbnail';
import { CDN_BASE_URL } from '../../../../../../../../../.storybook/utils';
import Icon, { IconSizes, IconColors } from '../../component/Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import { ACCEPTED_MEDIA } from '../../../constants';
import endpoints from '../../define/endpoints';
import { getVideoCover } from '../../hooks/getVideoCover';

const DropZone = ({
  classNames,
  label,
  icon,
  handleFileUpload,
  handleFileDeletion,
  maxNumberOfFiles = 10,
  maxFileSize,
  isDisabled,
  hasFiles,
  setHasFiles,
  draftMedia = [],
  uploadStatusChange,
}) => {
  const videoExtensions = ['.mp4', '.webm', '.avi', '.mov'];

  const [files, setFiles] = useState([]);
  const [draftFiles, setDraftFiles] = useState([]);
  const [draftFilesPreviews, setDraftFilesPreviews] = useState([]);
  const [numberOfFiles, setNumberOfFiles] = useState(0);
  const [error, setError] = useState(null);
  const baseUrl = endpoints.bucket;
  const [fileLoadingCounter, setFileLoadingCounter] = useState(0);

  const DROPZONE_ERROR = {
    FILE_TYPE: 'file-invalid-type',
    FILE_SIZE: 'file-too-large',
    FILES_AMOUNT: 'too-many-files',
  };

  const hasValidationError = (fileRejections, errorCode) => {
    return fileRejections.some((fileRejection) =>
      fileRejection.errors.some((error) => error.code === errorCode)
    );
  };

  const hasVideoExtension = (src) => {
    const lower_case_src = src.toLowerCase();
    const extension = videoExtensions.find((ext) =>
      lower_case_src.endsWith(ext)
    );
    if (extension) {
      return extension;
    } else {
      return false;
    }
  };

  const convertMegaBytesToBytes = (megaBytes) => {
    const MB_UNIT_IN_BYTES = 1048576;
    return megaBytes * MB_UNIT_IN_BYTES;
  };
  const defaultMaxSize = maxFileSize
    ? convertMegaBytesToBytes(maxFileSize)
    : 104857600; // Default 100MB

  const defaultMaxFile = maxNumberOfFiles;
  const isMaxFilesExceeded = files.length + draftFiles.length >= defaultMaxFile;

  useEffect(() => {
    manageDraftFiles();
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  useEffect(() => {
    uploadStatusChange(!!fileLoadingCounter);
  }, [fileLoadingCounter]);

  useEffect(() => {
    (async () => {
      for (let i = 0; i < draftFiles.length; i++) {
        const media = draftFiles[i];
        if (media?.url) {
          if (hasVideoExtension(media.url)) {
            draftFilesPreviews[i] = await getVideoCover(
              `${endpoints.bucket}${media.url}`
            );
          } else {
            draftFilesPreviews[i] = `${endpoints.bucket}${media.url}`;
          }
          //console.log('preview', draftFilesPreviews[i])
        }
      }
      //console.log('draftFilesPreviews', draftFilesPreviews);
      setDraftFilesPreviews([...draftFilesPreviews]);
    })();
  }, [draftFiles]);

  const manageDraftFiles = useCallback(() => {
    if (draftMedia.length === 0) return;

    setHasFiles(true);
    setNumberOfFiles(draftMedia.length);
    setDraftFiles(draftMedia);
  }, [draftMedia]);

  const handleDeletion = (file) => {
    if (numberOfFiles - 1 === 0) {
      setHasFiles(false);
    }

    setError(null);
    setNumberOfFiles(numberOfFiles - 1);
    file && handleFileDeletion(file);
  };

  // function that check the new file if it already exists
  const checkDuplicatedFiles = (newFile) => {
    return (
      files.some(
        (hasFile) => hasFile.name === newFile.name && hasFile.size === newFile.size
      ) || draftFiles.some(
        (hasFile) => hasFile.name === newFile.name && hasFile.size === newFile.size
      )
    );
  };

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (isDisabled || isMaxFilesExceeded) return;
      setError(null);

      // Dropped group of files is > maxNumberOfFiles
      if (
        fileRejections.length > maxNumberOfFiles ||
        hasValidationError(fileRejections, DROPZONE_ERROR.FILES_AMOUNT)
      ) {
        return setError(TRANSLATIONS.FILE_LENGTH_ALERT);
      }

      // At least one file is > defaultMaxSize || has an invalid type
      if (
        hasValidationError(fileRejections, DROPZONE_ERROR.FILE_SIZE) ||
        hasValidationError(fileRejections, DROPZONE_ERROR.FILE_TYPE)
      ) {
        return setError(TRANSLATIONS.FILE_ALERT);
      }

      // dropped files summed with accepted > maxNumberOfFiles
      let sum = files.length + draftFiles.length;
      if (acceptedFiles.length + sum > maxNumberOfFiles) {
        return setError(TRANSLATIONS.FILE_LENGTH_ALERT);
      }

      // check and filter the duplicated files 
      const uniqueFiles = acceptedFiles.filter((file) => !checkDuplicatedFiles(file));

      // in case of duplicated file return without showing any error message
      if (uniqueFiles.length === 0) {
        return;
      }

      // replaced with the correct filter ones and updated the hook dependencies
      setFileLoadingCounter(fileLoadingCounter + uniqueFiles.length);

      const updatedFiles = uniqueFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          status: handleFileUpload(file),
        })
      );

      setNumberOfFiles(numberOfFiles + uniqueFiles.length);
      setFiles((prev) => [...prev, ...updatedFiles]);
    },
    [handleFileUpload, numberOfFiles, files, draftFiles]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      ...ACCEPTED_MEDIA,
    },
    maxFiles: defaultMaxFile,
    maxSize: defaultMaxSize,
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const thumbnails = () => [
    ...draftFiles.map((file, key) => {
      const previewUrl = draftFilesPreviews[key];
      return (
        <div
          key={file.id}
          className="thumbnail"
          style={{
            // In case of video preview it only gets the background color
            background: `url("${previewUrl}") lightgray 50% / cover no-repeat`,
          }}
        >
          <div
            className={cn(
              'background--mask absolute z-index-1 w-100% h-100% radius-xs'
            )}
          />
          <div
            className="delete--button"
            onClick={() => {
              setDraftFiles(draftFiles.filter((item) => file !== item));
              handleDeletion(file);
            }}
          >
            <Icon name="close" size={IconSizes[8]} color={IconColors.regular} />
          </div>
        </div>
      );
    }),
    ...files.map((file, key) => {
      const size = file.size;
      return (
        <Thumbnail
          key={key}
          file={file}
          setFiles={setFiles}
          handleDelete={(file) => handleDeletion(file, size)}
          setFileLoadingCounter={setFileLoadingCounter}
          setError={setError}
        />
      );
    }),
  ];

  const renderLabel = () => {
    if (typeof label === 'string') {
      return (
        <div className="drop-zone__label">
          <Typography variant="p3" state={TypographyStates['neutral-light']}>
            {label}
          </Typography>
        </div>
      );
    }
    return <div className="drop-zone__label">{label}</div>;
  };

  const renderIcon = () => {
    return (
      <div className="drop-zone__icon">
        {icon ? (
          icon
        ) : (
          <Icon name="add-media" size={IconSizes[!hasFiles ? 16 : 24]} />
        )}
      </div>
    );
  };

  if (isDisabled) {
    return (
      <div
        className={cn('drop-zone__input', { disabled: isDisabled }, classNames)}
      >
        {renderIcon()}
        {renderLabel()}
      </div>
    );
  }

  return (
    <div className="drop-zone--container d-flex flex-column gap-3xs">
      <div className="drop-zone--inner-container d-flex flex-wrap gap-3xs">
        {hasFiles && thumbnails()}
        {numberOfFiles < defaultMaxFile && (
          <div
            className={cn(
              'drop-zone__input',
              { thumbnail: hasFiles },
              classNames
            )}
            {...getRootProps()}
            onClick={open}
          >
            <input {...getInputProps()} />
            {/* {isDragActive ? <p>Drop the files here ...</p> : label} */}
            {renderIcon()}
            {!hasFiles && renderLabel()}
          </div>
        )}
      </div>
      <div className="drop-zone__counter d-flex justify-end flex-1-1-auto">
        <Typography variant="p4" state={TypographyStates['neutral-light']}>
          {numberOfFiles}/{defaultMaxFile}
        </Typography>
      </div>
      {error && (
        <div className="drop-zone__error">
          <Typography
            variant="p4"
            weight={TypographyWeights.light}
            state={TypographyStates.error}
          >
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
};

DropZone.propTypes = {
  classNames: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  handleFileUpload: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  /** Default value = 10 */
  maxNumberOfFiles: PropTypes.number,
  /** Max file size in MB - Default value = 100MB */
  maxFileSize: PropTypes.number,
  handleFileDeletion: PropTypes.func,
};

export default DropZone;